import { Injectable } from '@angular/core';
import { GemsHttpService } from "../../../shared/services/gems-http.service";
import { constants } from '../../../constants/all.constants';
import { Organization } from '../../../domains/Organization';
import { Addendum } from '../../../domains/Addendum';
import { AdoptionAgreement } from '../../../domains/AdoptionAgreement';
import { ProcessedDistribution } from '../../../domains/ProcessedDistribution'
import { RetirementSPDSMM } from '../../../domains/RetirementSPDSMM';
import { SafeHarbor } from '../../../domains/SafeHarbor';
import { OrganizationList } from 'client/app/domains/OrganizationList';
import { PendingAcknowledgement } from 'client/app/domains/PendingAcknowledgement';
import { BatchAcknowledgementPrintResponseItem } from 'client/app/domains/BatchAcknowledgementPrintResponseItem';
import { ProcessedAcknowledgement } from 'client/app/domains/ProcessedAcknowledgement';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RetirementPlanDocumentsService {
  constructor(private gemsHttp: GemsHttpService) {
  }

  loadData(personId) {
    const url = constants.apiRoutes.retirementPlanDocuments.GetLux2 + personId;
    return this.gemsHttp.get(url).pipe(
      map(mapDocuments),
      catchError(err => {
        console.error('error', err);
        return of([]);
      })
    );
  }

  submitSpdSmmAcknowledgements(obj) {
    const url = constants.apiRoutes.retirementPlanDocuments.SubmitSpdSmmAcknowledgementLux2;
    return this.gemsHttp.post(url, obj).pipe(
      catchError(err => {
        console.error('error', err);
        return of([]);
      })
    );
  }

  submitSafeHarborAcknowledgements(obj) {
    const url = constants.apiRoutes.retirementPlanDocuments.SubmitSafeHarborAcknowledgementLux2;
    return this.gemsHttp.post(url, obj).pipe(
      catchError(err => {
        console.error('error', err);
        return of([]);
      })
    );
  }
  loadPrintBatchAcknowledgement(obj) {
    const url = constants.apiRoutes.retirementPlanDocuments.GetAcknowledgementsForPrintLux2;
    return this.gemsHttp.post(url, obj).pipe(
      map(mapBatchAcknowledgements),
      catchError(err => {
        console.error('error', err);
        return of([]);
      })
    );
  }
}

function mapBatchAcknowledgements(response: any) {
  const data = response.result;
  const batchAcknowledgements = [];

  for (const item of data) {

    const processedAcknowledgement: ProcessedAcknowledgement =
    {
      SubgroupId: item.processedAcknowledgement.subgroupId,
      EffectiveDate: item.processedAcknowledgement.effectiveDate,
      EffectiveDateDisplay: item.processedAcknowledgement.effectiveDateDisplay,
      PlanDocument: item.processedAcknowledgement.planDocument,
      Type: item.processedAcknowledgement.type,
      Status: item.processedAcknowledgement.status,
      SubgroupName: item.processedAcknowledgement.subgroupName
    }

    const batchAcknowledgement: BatchAcknowledgementPrintResponseItem =
    {
      Id: item.Id,
      RetirementSpdId: item.retirementSpdId,
      Can10PercentRead: item.can10PercentRead,
      Can25PercentRead: item.can25PercentRead,
      Can500Read: item.can500Read,
      Comments: item.comments,
      DistributionDate: item.distributionDate,
      FirstName: item.firstName,
      LanguageForLargePlan: item.languageForLargePlan,
      LanguageForSmallPlan: item.languageForSmallPlan,
      LastName: item.lastName,
      MethodOfDelivery: item.methodOfDelivery,
      NumberWhoReadLargePlan: item.numberWhoReadLargePlan,
      NumberWhoReadSmallPlan: item.numberWhoReadSmallPlan,
      ParticipatCount: item.participatCount,
      ProcessedAcknowledgement: processedAcknowledgement,
      SubmissionDate: item.submissionDate,
      TransactionId: item.transactionId,
      HasSmms: false,
      HasSpds: false,
      HasSh: false,
      MoreThan100Participants: false,
      FewerThan100Participants: false
    }
    batchAcknowledgements.push(batchAcknowledgement);
  }

  let hasSmms = false;
  let hasSpds = false;
  let hasSh = false;
  let moreThan100Participants = false;
  let fewerThan100Participants = false;
  for (const acknowledgement of batchAcknowledgements) {
    if (acknowledgement.ProcessedAcknowledgement.Type === 'SPD' && hasSpds === false) {
      hasSpds = true;
    }
    if (acknowledgement.ProcessedAcknowledgement.Type === 'SMM' && hasSpds === false) {
      hasSmms = true;
    }
    if (acknowledgement.ProcessedAcknowledgement.Type === 'Safe Harbor' && hasSpds === false) {
      hasSh = true;
    }
    if (acknowledgement.ParticipatCount < 100 && fewerThan100Participants === false) {
      fewerThan100Participants = true;
    }
    if (acknowledgement.ParticipatCount >= 100 && moreThan100Participants === false) {
      moreThan100Participants = true;
    }
  }

  for (const acknowledgement of batchAcknowledgements) {
    acknowledgement.HasSpds = hasSpds;
    acknowledgement.HasSmms = hasSmms;
    acknowledgement.HasSh = hasSh;
    acknowledgement.MoreThan100Participants = moreThan100Participants;
    acknowledgement.FewerThan100Participants = fewerThan100Participants;
  }

  return batchAcknowledgements;
}



function mapDocuments(response: any): OrganizationList {
  const data = response.result;
  const organizations: Organization[] = [];
  let safeHarborDocuments: SafeHarbor[] = [];
  let adoptionAgreementDocuments: AdoptionAgreement[] = [];
  let addendums: Addendum[] = [];
  let retirementSpds: RetirementSPDSMM[] = [];
  let retirementSmms: RetirementSPDSMM[] = [];
  let pendingShAcknowledgements: PendingAcknowledgement[] = [];
  let pendingSpdAcknowledgements: PendingAcknowledgement[] = [];
  let pendingSmmAcknowledgements: PendingAcknowledgement[] = [];

  for (const element of data) {
    const organization = element;
    for (const safeHarborDocument of organization.safeHarborDocuments) {

      const safeHarbor: SafeHarbor = {
        Name: safeHarborDocument.name.replace(/\&amp;/g, "&"),
        EffectiveDate: safeHarborDocument.effectiveDate,
        EffectiveDateDisplay: safeHarborDocument.effectiveDateDisplay,
        ReleaseDate: safeHarborDocument.releaseDate,
        Type: safeHarborDocument.type,
        DownloadLink: safeHarborDocument.name.replace(/\&amp;/g, "&") // WRONG
      }

      safeHarborDocuments.push(safeHarbor);
    }

    for (const adoptionAgreementDocument of organization.adoptionAgreementDocuments) {
      const addendumDocuments = adoptionAgreementDocument.addendumDocuments;

      for (const addendumDocument of addendumDocuments) {
        const addendum: Addendum = {
          PlanDocument: addendumDocument.planDocument,
          DocumentType: addendumDocument.documentType,
          EffectiveDate: addendumDocument.effectiveDate,
          EffectiveDateDisplay: addendumDocument.effectiveDateDisplay,
          PlanNumber: addendumDocument.planNumber,
          Status: addendumDocument.documentStatus,
          DownloadLink: addendumDocument.fileName.replace(/\&amp;/g, "&"),
          CanDownload: addendumDocument.canDownload
        }

        addendums.push(addendum);
      }

      const adoptionAgreement: AdoptionAgreement =
      {
        Id: adoptionAgreementDocument.id,
        PlanDocument: adoptionAgreementDocument.planDocument,
        DocumentType: adoptionAgreementDocument.documentType,
        EffectiveDate: adoptionAgreementDocument.effectiveDate,
        EffectiveDateDisplay: adoptionAgreementDocument.effectiveDateDisplay,
        PlanNumber: adoptionAgreementDocument.planNumber,
        Status: adoptionAgreementDocument.documentStatus,
        DownloadLink: adoptionAgreementDocument.fileName.replace(/\&amp;/g, "&"),
        CanDownload: adoptionAgreementDocument.canDownload,
        Addendums: addendums,
        AddendumsVisible: false
      }

      adoptionAgreementDocuments.push(adoptionAgreement);

      addendums = [];
    }

    for (const retirementSpd of organization.retirementSpds) {
      const retirementSmms: RetirementSPDSMM[] = [];
      for (const retirementSmm of retirementSpd.smms) {

        const smm: RetirementSPDSMM = {
          PlanType: retirementSmm.planType,
          PlanDocument: retirementSmm.planDocument.replace(/\&lt;/g, "<").replace(/\&gt;/g, ">").replace(/&quot;/g, "\""),
          EffectiveDate: retirementSmm.effectiveDate,
          EffectiveDateDisplay: retirementSmm.effectiveDateDisplay,
          ReleaseDate: retirementSmm.releaseDate,
          DocumentType: retirementSmm.documentType,
          DownloadLink: retirementSmm.name,
          Smms: [],
          SmmsVisible: false
        };
        retirementSmms.push(smm);
      }

      const spd: RetirementSPDSMM = {
        PlanType: retirementSpd.planType,
        PlanDocument: retirementSpd.planDocument.replace(/\&lt;/g, "<").replace(/\&gt;/g, ">").replace(/&quot;/g, "\""),
        EffectiveDate: retirementSpd.effectiveDate,
        EffectiveDateDisplay: retirementSpd.effectiveDateDisplay,
        ReleaseDate: retirementSpd.releaseDate,
        DocumentType: retirementSpd.documentType,
        DownloadLink: retirementSpd.name, // MISSING INFO
        Smms: retirementSmms,
        SmmsVisible: false
      };

      retirementSpds.push(spd);
    }

    for (const pending of organization.acknowledgements.pendingShAcknowledgements) {
      const acknowledgement: PendingAcknowledgement = {
        DownloadLink: pending.downloadLink.replace(/\&amp;/g, "&"),
        PlanDocument: pending.planDocument,
        EffectiveDate: pending.effectiveDate,
        EffectiveDateDisplay: pending.effectiveDateDisplay,
        Organization: pending.organization,
        Checked: true,
        Type: pending.type,
        Id: pending.id
      };

      pendingShAcknowledgements.push(acknowledgement);
    }
    for (const pending of organization.acknowledgements.pendingSpdAcknowledgements) {
      const acknowledgement: PendingAcknowledgement = {
        DownloadLink: pending.downloadLink.replace(/\&amp;/g, "&"),
        PlanDocument: pending.planDocument,
        EffectiveDate: pending.effectiveDate,
        EffectiveDateDisplay: pending.effectiveDateDisplay,
        Organization: pending.organization,
        Checked: true,
        Type: pending.type,
        Id: pending.id
      };

      pendingSpdAcknowledgements.push(acknowledgement);
    }
    for (const pending of organization.acknowledgements.pendingSmmAcknowledgements) {
      const acknowledgement: PendingAcknowledgement = {
        DownloadLink: pending.downloadLink.replace(/\&amp;/g, "&"),
        PlanDocument: pending.planDocument,
        EffectiveDate: pending.effectiveDate,
        EffectiveDateDisplay: pending.effectiveDateDisplay,
        Organization: pending.organization,
        Checked: true,
        Type: pending.type,
        Id: pending.id
      };

      pendingSmmAcknowledgements.push(acknowledgement);
    }

    const subgroup: Organization = {
      Name: organization.organization,
      SafeHarborDocuments: safeHarborDocuments,
      AdoptionAgreementDocuments: adoptionAgreementDocuments,
      RetirementSpds: retirementSpds,
      RetirementSmms: retirementSmms,
      PendingShAcknowledgements: pendingShAcknowledgements,
      PendingSmmAcknowledgements: pendingSmmAcknowledgements,
      PendingSpdAcknowledgements: pendingSpdAcknowledgements,
      PendingShAcknowledgementsVisible: false,
      PendingSmmAcknowledgementsVisible: false,
      PendingSpdAcknowledgementsVisible: false,
      SmmCheckedAll: false,
      SmmStartEnabled: true,
      SpdCheckedAll: false,
      SpdStartEnabled: true,
      Status: '',
      Expanded: false,
      Open: false,
      SpdMaster: true,
      SmmMaster: true
    }

    organizations.push(subgroup);
    safeHarborDocuments = [];
    adoptionAgreementDocuments = [];
    retirementSpds = [];
    retirementSmms = [];
    pendingShAcknowledgements = [];
    pendingSmmAcknowledgements = [];
    pendingSpdAcknowledgements = [];
  }

  const processedDistributions: ProcessedDistribution[] = [];
  for (var i = 0; i < data.length; i++) {
    var organization = data[i];
    var id = 0;
    if (organization.Id)
      id = organization.Id;

    for (var j = 0; j < organization.acknowledgements.processedSpdAcknowledgements.length; j++) {
      var acknowledgement = organization.acknowledgements.processedSpdAcknowledgements[j];

      const processedDistribution: ProcessedDistribution = {
        Id: id,
        RetirementSpdId: acknowledgement.retirementSpdId,
        Organization: acknowledgement.processedAcknowledgement.subgroupName,
        PlanDocument: acknowledgement.processedAcknowledgement.planDocument,
        Type: acknowledgement.processedAcknowledgement.type,
        EffectiveDate: acknowledgement.processedAcknowledgement.effectiveDate,
        EffectiveDateDisplay: acknowledgement.processedAcknowledgement.effectiveDateDisplay,
        DistributionDate: acknowledgement.distributionDate,
        DistributionMethod: acknowledgement.methodOfDelivery,
        Comments: acknowledgement.comments,
        SubmittedByFullName: acknowledgement.firstName + " " + acknowledgement.lastName,
        FewerThan100Participants: acknowledgement.participatCount < 100,
        English25Percent: acknowledgement.can25PercentRead === 'Yes',
        NumParticipantsLesser: acknowledgement.numberWhoReadSmallPlan,
        NonEnglishLanguageLesser: acknowledgement.languageForSmallPlan,
        MoreThan100Participants: acknowledgement.participatCount >= 100,
        English500: acknowledgement.can500Read === 'Yes',
        English10Percent: acknowledgement.can10PercentRead === 'Yes',
        NumParticipantsMore: acknowledgement.numberWhoReadLargePlan,
        NonEnglishLanguageMore: acknowledgement.languageForLargePlan,
        DownloadLink: acknowledgement.downloadLink,
        ParticipantNumber: acknowledgement.participatCount,
        SubmissionDate: new Date(Date.parse(acknowledgement.submissionDate)).toLocaleDateString(),
        Status: 'SENT',
        Expanded: false
      };
      processedDistributions.push(processedDistribution)
    }
  }

  const organizationList: OrganizationList = {
    Organizations: organizations,
    ProcessedDistributions: processedDistributions
  }

  return organizationList;
}

